import React, { useEffect, useState, useRef, useMemo } from "react";
import { v4 } from "uuid";
import { cloneDeep } from "lodash";
import { useTranslation } from "react-i18next";
import { DataTable, DataTableFilterMeta } from "primereact/datatable";
import { Column } from "primereact/column";
import { FilterMatchMode } from "primereact/api";
import { Dropdown } from "primereact/dropdown";
import { Toast } from "primereact/toast";
import { ProgressSpinner } from "primereact/progressspinner";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { Warehouse, WarehouseStatus } from "../../models/Warehouse";
import { getAllWarehouses } from "../../services/WarehouseService";
import { NewWarehouseForm } from "../../forms/NewWarehouseForm/NewWarehouseForm";
import "./WarehousesPage.scss"; 

export const WarehousesPage = () => {
  const { t } = useTranslation();
  const toast = useRef<Toast>(null);

  const [processing, setProcessing] = useState(false);
  const [allWarehouses, setAllWarehouses] = useState<Warehouse[]>([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState<
    Warehouse | undefined
  >();
  const [showForm, setShowForm] = useState<string>("");

  const [filters, setFilters] = useState<DataTableFilterMeta>({
    code: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    address: { value: null, matchMode: FilterMatchMode.CONTAINS },
    cityName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    weighbridgeModel: { value: null, matchMode: FilterMatchMode.CONTAINS },
    calculateCalo: { value: null, matchMode: FilterMatchMode.EQUALS },
    status: { value: null, matchMode: FilterMatchMode.EQUALS },
  });

  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(10);

  const ensureSuccess = (resp: any) => {
    if (resp?.data?.status === "ERROR") {
      throw new Error(resp?.data?.message ?? "Greška");
    }
  };

  const refreshWarehouses = async () => {
    setProcessing(true);
    try {
      const resp = await getAllWarehouses();
      ensureSuccess(resp);

      const list: Warehouse[] = resp?.data?.result ?? resp?.data ?? [];
      const normalized = list.map((w: any) => ({
        ...w,
        cityName: w?.city?.name ?? "",
      }));

      setAllWarehouses(normalized);
    } catch (e: any) {
      toast.current?.show({
        severity: "error",
        detail:
          e?.response?.data?.message ??
          String(t("Greška pri učitavanju magacina")),
        life: 3500,
      });
    } finally {
      setProcessing(false);
    }
  };

  useEffect(() => {
    refreshWarehouses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tableValue = useMemo(() => allWarehouses, [allWarehouses]);

  const calculateCaloOptions = useMemo(
    () => [
      { label: t("Sve"), value: null },
      { label: t("Da"), value: true },
      { label: t("Ne"), value: false },
    ],
    [t]
  );

  const statusOptions = useMemo(
    () => [
      { label: t("Sve"), value: null },
      { label: t("Zaključan"), value: WarehouseStatus.Locked },
      { label: t("Otključan"), value: WarehouseStatus.Open },
    ],
    [t]
  );

  const calculateCaloFilterElement = (options: any) => {
    return (
      <Dropdown
        value={options.value}
        options={calculateCaloOptions}
        onChange={(e) => options.filterApplyCallback(e.value)}
        placeholder={String(t("Izaberite"))}
        className="p-column-filter"
        showClear={false}
      />
    );
  };

  const statusFilterElement = (options: any) => {
    return (
      <Dropdown
        value={options.value}
        options={statusOptions}
        onChange={(e) => options.filterApplyCallback(e.value)}
        placeholder={String(t("Izaberite"))}
        className="p-column-filter"
        showClear={false}
      />
    );
  };

  const cityBody = (row: any) => <span>{row?.city?.name ?? ""}</span>;

  const nameBody = (row: any) => (
    <span>{String(row?.name ?? "").toUpperCase()}</span>
  );

  const calculateCaloBody = (row: any) => (
    <span>{row?.calculateCalo ? t("Da") : t("Ne")}</span>
  );

  const statusBody = (row: any) => {
    const isLocked = row?.status === WarehouseStatus.Locked;
    return (
      <div className="action-button">
        <ButtonInput
          addStyle={{
            border: "none",
            background: !isLocked ? "#198754" : "#DC3545",
          }}
          iconAlign="right"
          icon="bi bi-exclamation-octagon"
          labelText={isLocked ? t("Zaključan") : t("Otključan")}
          onButtonClick={() => {}}
        />
      </div>
    );
  };

  const actionsBody = (row: Warehouse) => {
    return (
      <div className="action-button">
        <ButtonInput
          addStyle={{ border: "none", background: "#198754" }}
          labelText={""}
          icon="bi bi-pencil"
          onButtonClick={() => {
            setSelectedWarehouse(cloneDeep(row));
            setShowForm(v4());
          }}
        />
      </div>
    );
  };

  return (
    <PageLayout>
      <Toast ref={toast} position="top-right" />

      <div className="table-wrapper">
        <div className="table-header">
          <div className="header-left">
            <i
              className="bi bi-shop-window header-title-icon"
              style={{
                fontSize: 14,
                marginLeft: "1rem",
              }}
            />
            <span className="header-left-title">{t("Lista magacina")}</span>
          </div>

          <div className="header-right">
            <NewWarehouseForm
              buttonLabel={String(t("Dodaj magacin"))}
              buttonIcon="bi bi-shop-window"
              show={showForm}
              warehouse={selectedWarehouse}
              buttonStyle={{
                border: "none",
                background: "#ffffff",
                color: "#6C757D",
                marginRight: "1rem",
                margin: "0 1rem 0 0",
              }}
              refreshingData={refreshWarehouses}
            />
          </div>
        </div>

        {processing ? (
          <ProgressSpinner />
        ) : (
          <div className="table-body">
            <DataTable
              value={tableValue}
              dataKey="id"
              scrollable
              scrollHeight="80vh"
              stripedRows
              showGridlines
              removableSort
              sortMode="single"
              filterDisplay="row"
              filters={filters}
              onFilter={(e) => {
                setFilters(e.filters as DataTableFilterMeta);
                setFirst(0);
              }}
              paginator
              first={first}
              rows={rows}
              onPage={(e) => {
                setFirst(e.first);
                setRows(e.rows);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
              currentPageReportTemplate=""
              emptyMessage={t("Nema magacina za zadate kriterijume")}
            >
              <Column
                field="code"
                header={t("Šifra")}
                sortable
                filter
                style={{ minWidth: "140px" }}
              />
              <Column
                field="name"
                header={t("Naziv magacina")}
                body={nameBody}
                sortable
                filter
                style={{ minWidth: "240px" }}
              />
              <Column
                field="address"
                header={t("Adresa magacina")}
                sortable
                filter
                style={{ minWidth: "240px" }}
              />
              <Column
                field="cityName"
                header={t("Mesto/Naselje")}
                body={cityBody}
                sortable
                filter
                style={{ minWidth: "200px" }}
              />
              <Column
                field="weighbridgeModel"
                header={t("Model vage")}
                sortable
                filter
                style={{ minWidth: "180px" }}
              />
              <Column
                field="calculateCalo"
                header={t("Obračun kala")}
                body={calculateCaloBody}
                filter
                filterElement={calculateCaloFilterElement}
                showFilterMenu={false}
                style={{ minWidth: "160px" }}
              />
              <Column
                field="status"
                header={t("Status magacina")}
                body={statusBody}
                filter
                filterElement={statusFilterElement}
                showFilterMenu={false}
                style={{ minWidth: "180px" }}
              />
              <Column
                header={t("Opcije")}
                body={actionsBody}
                style={{ minWidth: "120px" }}
              />
            </DataTable>
          </div>
        )}
      </div>
    </PageLayout>
  );
};
